import type { TextFieldProps } from './TextField';
import type { TextInputProps } from 'components/TextInput/types';

/**
 * Maps the legacy MUI `TextField` props onto the Design System 2.0 `TextInput` API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at the 73-odd files that reach this component (most of them through
 * `ReactHookForm/TextField`); retiring the flag means deleting it and letting call sites speak the
 * DS vocabulary directly.
 *
 * Returning `null` means "the DS input cannot express this field" and sends the caller back to MUI.
 * A silent best-effort mapping is worse than no mapping here: an input is where a wrong size or a
 * dropped slot prop is least visible at review time and most disruptive in use.
 */
type DsProps = Pick<TextInputProps, 'label' | 'helperText' | 'error' | 'leftIcon'> &
  Pick<TextInputProps, 'value' | 'defaultValue'>;

/**
 * MUI types `value` and `defaultValue` as `unknown`, because its input accepts whatever the caller
 * hands it and stringifies at the DOM boundary. The DS control is typed to what an `<input>` can
 * actually take, so the value has to be narrowed rather than cast — a cast here would let an object
 * through to `value=` and render `[object Object]`.
 */
const isControlValue = (value: unknown): value is string | number | readonly string[] =>
  typeof value === 'string' || typeof value === 'number' || Array.isArray(value);

/**
 * Cases the DS input cannot express, all of which stay on MUI:
 *
 *   - anything not `fullWidth` — the DS root is `w-full` unconditionally
 *     (`textInputRootRecipe`), whereas an MUI field without `fullWidth` takes its own intrinsic
 *     width. Mapping those would stretch the field and reflow its neighbours, which is the same
 *     silent-resize trap `Buttons/Button/toDsProps` avoids by matching on height rather than name.
 *   - `multiline` / `rows` / `maxRows` — DS `TextInput` wraps a `Field.Control`, which is an
 *     `<input>`. The refreshed set draws no textarea, so there is nothing to map onto.
 *   - `sx` — an Emotion prop with no effect on a Tailwind component. Accepting it would silently
 *     drop the styling rather than fail.
 *   - `InputProps` / `inputProps` — these address MUI's own slots (adornments, `readOnly`, input
 *     attributes). The DS input has no such internals, so they would vanish.
 *   - `size` — the refreshed input has a single height and no size axis.
 *   - a non-string or absent `label` — DS requires `label: string`, because the floating label is
 *     the field's only visible name. The legacy prop is an optional `ReactNode`.
 *   - `shrinkLabel` — the DS label shrinks off the presence of a value on its own and exposes no
 *     override, so honouring this would mean pinning a label the component intends to animate.
 *   - `wrapHint={false}` — a `noWrap` on the MUI hint (`FormFieldHint`). The DS helper text has no
 *     equivalent, and forcing it would clip the message instead of wrapping it.
 *   - `variant` other than `outlined` — the wrapper hardcodes `outlined` on the MUI element, so a
 *     caller passing `filled` or `standard` today already gets something the DS cannot draw.
 *   - **`suffix`** — the newest entry, and the one that reads as a regression until you know why.
 *     It used to map onto `rightIcon`, a plain `ReactNode` slot. The refreshed field draws its
 *     trailing affordance as a `Button` (`Size=ExtraSmall, Type=Muted, iconOnly=Yes`), so the DS
 *     side is now `rightButton` + `rightButtonLabel` + `onRightButtonClick` — a named, handled
 *     control. A legacy `suffix` is an unnamed, unhandled node, so there is nothing to derive the
 *     other two from: mapping it would render a button that a screen reader announces as "button"
 *     and that does nothing when pressed. That is precisely the silent best-effort this file
 *     exists to refuse.
 *
 * `prefix` still maps, onto `leftIcon`: both are decorative `ReactNode` glyphs, and the legacy
 * wrapper only ever wrapped it in an `InputAdornment` for positioning.
 *
 * Costs nothing today, which is worth recording so the trade-off can be re-judged rather than
 * re-derived: exactly one call site in the app passes `suffix` (`question/.../ChoiceItem`), and it
 * also passes `InputProps`, so it was already returning `null` here and already staying on MUI. A
 * future field that wants a real trailing button should speak the DS vocabulary directly — which is
 * what retiring this mapper means anyway (HNS-264).
 */
export const toDsProps = ({
  label,
  hint,
  error,
  fullWidth,
  multiline,
  rows,
  maxRows,
  size,
  sx,
  variant,
  InputProps,
  inputProps,
  prefix,
  suffix,
  shrinkLabel,
  wrapHint = true,
  value,
  defaultValue,
}: TextFieldProps): DsProps | null => {
  if (!fullWidth) {
    return null;
  }

  if (
    (value !== undefined && !isControlValue(value)) ||
    (defaultValue !== undefined && !isControlValue(defaultValue))
  ) {
    return null;
  }

  if (multiline || rows !== undefined || maxRows !== undefined) {
    return null;
  }

  if (sx || InputProps || inputProps || size || shrinkLabel || !wrapHint) {
    return null;
  }

  // See the `suffix` entry above: there is no longer a DS slot a bare node can fill.
  if (suffix) {
    return null;
  }

  if (variant !== undefined && variant !== 'outlined') {
    return null;
  }

  if (typeof label !== 'string') {
    return null;
  }

  return { label, helperText: hint, error, leftIcon: prefix, value, defaultValue };
};
